import type { ComposerTranslation } from 'vue-i18n'
import { ThreatLevel } from '@/enum/threat-level'
import { AttackType } from '@/enum/attack-type'

export interface ThreatTag {
  /** 等级 */
  level: number
  /** 标题 i18n 键：`threatTag.slug.<slug>.title` */
  titleI18nKey: string
  /** 攻击类型 */
  attackType: number
  /** 描述 i18n 键：`threatTag.slug.<slug>.describe` */
  describeI18nKey: string
}

function threatTagTitleKey (slug: string): string {
  return `threatTag.slug.${slug}.title`
}

function threatTagDescribeKey (slug: string): string {
  return `threatTag.slug.${slug}.describe`
}

function meta (
  slug: string,
  level: ThreatLevel,
  attackType: AttackType
): ThreatTag {
  return {
    level,
    attackType,
    titleI18nKey: threatTagTitleKey(slug),
    describeI18nKey: threatTagDescribeKey(slug),
  }
}

/** 威胁标签（文案走 `threatTag.slug.*`，展示处用 `t(titleI18nKey)`） */
export const THREAT_TAG: { [key: string]: ThreatTag } = {
  'proxy': meta('proxy', ThreatLevel.low, AttackType.suspiciousTraffic),
  'tor-node': meta('tor-node', ThreatLevel.low, AttackType.suspiciousTraffic),
  'suspicious': meta('suspicious', ThreatLevel.low, AttackType.suspiciousTraffic),
  'spamming': meta('spamming', ThreatLevel.low, AttackType.offendingContent),
  'violation': meta('violation', ThreatLevel.low, AttackType.offendingContent),
  'porn': meta('porn', ThreatLevel.low, AttackType.offendingContent),
  'scanner-node': meta('scanner-node', ThreatLevel.low, AttackType.scanDetection),
  'gambling': meta('gambling', ThreatLevel.low, AttackType.offendingContent),
  'compromised': meta('compromised', ThreatLevel.low, AttackType.suspiciousTraffic),
  'rogueware': meta('rogueware', ThreatLevel.low, AttackType.riskSoftware),
  'clicker': meta('clicker', ThreatLevel.low, AttackType.riskSoftware),
  'hoax': meta('hoax', ThreatLevel.low, AttackType.riskSoftware),
  'risktool': meta('risktool', ThreatLevel.low, AttackType.riskSoftware),
  'attacks': meta('attacks', ThreatLevel.medium, AttackType.scanDetection),
  'bruteforce': meta('bruteforce', ThreatLevel.medium, AttackType.scanDetection),
  'phishing': meta('phishing', ThreatLevel.medium, AttackType.phishingAttack),
  'malware': meta('malware', ThreatLevel.medium, AttackType.trojanHorse),
  'malware-link': meta('malware-link', ThreatLevel.medium, AttackType.trojanHorse),
  'malicious': meta('malicious', ThreatLevel.medium, AttackType.suspiciousTraffic),
  'malicious-email': meta('malicious-email', ThreatLevel.medium, AttackType.phishingAttack),
  'botnet': meta('botnet', ThreatLevel.medium, AttackType.trojanHorse),
  'miner': meta('miner', ThreatLevel.medium, AttackType.miningAttack),
  'troj': meta('troj', ThreatLevel.medium, AttackType.trojanHorse),
  'downloader': meta('downloader', ThreatLevel.medium, AttackType.trojanHorse),
  'infector': meta('infector', ThreatLevel.medium, AttackType.trojanHorse),
  'spyware': meta('spyware', ThreatLevel.medium, AttackType.trojanHorse),
  'worm': meta('worm', ThreatLevel.medium, AttackType.trojanHorse),
  'c2': meta('c2', ThreatLevel.high, AttackType.trojanHorse),
  'rootkit': meta('rootkit', ThreatLevel.high, AttackType.trojanHorse),
  'exploit': meta('exploit', ThreatLevel.high, AttackType.advancedThreat),
  'apt': meta('apt', ThreatLevel.high, AttackType.advancedThreat),
  'ransomware': meta('ransomware', ThreatLevel.high, AttackType.ransomware),
  'backdoor': meta('backdoor', ThreatLevel.high, AttackType.trojanHorse),
}

/** 将接口下发的威胁类型 slug（如 spyware、ransomware）转为展示标题；未命中返回 undefined */
export function getThreatTagTitle (
  tag: string | number | undefined | null,
  t: ComposerTranslation
): string | undefined {
  if (tag == null || tag === '') return undefined
  const k = String(tag).trim().toLowerCase()
  const row = THREAT_TAG[k]
  return row ? t(row.titleI18nKey) : undefined
}

/** 威胁类型描述；未命中返回 undefined */
export function getThreatTagDescribe (
  tag: string | number | undefined | null,
  t: ComposerTranslation
): string | undefined {
  if (tag == null || tag === '') return undefined
  const k = String(tag).trim().toLowerCase()
  const row = THREAT_TAG[k]
  return row ? t(row.describeI18nKey) : undefined
}

export function getThreatTagOptions (t: ComposerTranslation): { label: string; value: string | undefined }[] {
  return [
    { label: t('threatTag.all'), value: undefined },
    ...Object.keys(THREAT_TAG).map(key => ({
      value: key,
      label: t(THREAT_TAG[key].titleI18nKey),
    })),
  ]
}
